/**
 * Atomic Data Table Component
 *
 * Displays demo atomic shift-daily data in a sortable, paginated table
 * Uses TanStack React Table v8 with standard HTML table elements
 * Adapts columns based on view mode (shift-level vs daily aggregate)
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { aggregateDailyData } from '../utils/aggregateDailyData';

/**
 * AtomicDataTable Component
 * @param {Object} props - Component props
 * @param {Array} props.data - Raw filtered data array (shift-level records)
 * @param {Array} props.locations - Array of location objects {id, name}
 * @param {boolean} props.loading - Loading state
 */
const AtomicDataTable = ({ data = [], locations = [], loading = false }) => {
  // Internal view mode state
  const [viewMode, setViewMode] = useState('shift-level');

  /**
   * Handle view mode toggle
   */
  const handleViewModeChange = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  /**
   * Create location ID to name lookup map
   */
  const locationMap = useMemo(() => {
    const map = {};
    locations.forEach(loc => {
      map[loc.id] = loc.name || loc.id;
    });
    return map;
  }, [locations]);

  /**
   * Transform data based on view mode
   */
  const tableData = useMemo(() => {
    if (viewMode === 'daily-aggregate') {
      return aggregateDailyData(data);
    }
    return data;
  }, [data, viewMode]);
  /**
   * Define shift-level columns
   */
  const shiftLevelColumns = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        cell: (info) => info.getValue(),
        size: 100
      },
      {
        accessorKey: 'shift',
        header: 'Shift',
        cell: (info) => info.getValue(),
        size: 80
      },
      {
        accessorKey: 'locationId',
        header: 'Location',
        cell: (info) => locationMap[info.getValue()] || info.getValue(),
        size: 150
      },
      {
        accessorKey: 'netSales',
        header: 'Net Sales ($)',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 100
      },
      {
        accessorKey: 'checkCount',
        header: 'Check Count',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 100
      },
      {
        accessorKey: 'fohHours',
        header: 'FOH Hours',
        cell: (info) => (info.getValue() || 0).toFixed(1),
        size: 90
      },
      {
        accessorKey: 'bohHours',
        header: 'BOH Hours',
        cell: (info) => (info.getValue() || 0).toFixed(1),
        size: 90
      },
      {
        accessorKey: 'fohDollars',
        header: 'FOH Dollars ($)',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 110
      },
      {
        accessorKey: 'bohDollars',
        header: 'BOH Dollars ($)',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 110
      },
      {
        accessorKey: 'splh',
        header: 'SPLH ($)',
        cell: (info) => (info.getValue() || 0).toFixed(2),
        size: 90
      },
      {
        accessorKey: 'laborPct',
        header: 'Labor %',
        cell: (info) => ((info.getValue() || 0) * 100).toFixed(1),
        size: 80
      }
    ],
    [locationMap]
  );

  /**
   * Define daily aggregate columns
   */
  const dailyAggregateColumns = useMemo(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        cell: (info) => info.getValue(),
        size: 100
      },
      {
        accessorKey: 'locationCount',
        header: 'Locations',
        cell: (info) => info.getValue() || 0,
        size: 90
      },
      {
        accessorKey: 'netSales',
        header: 'Net Sales ($)',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 110
      },
      {
        accessorKey: 'checkCount',
        header: 'Check Count',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 100
      },
      {
        accessorKey: 'totalHours',
        header: 'Total Hours',
        cell: (info) => (info.getValue() || 0).toFixed(1),
        size: 100
      },
      {
        accessorKey: 'totalLaborDollars',
        header: 'Total Labor ($)',
        cell: (info) => Math.round(info.getValue()).toLocaleString(),
        size: 120
      },
      {
        accessorKey: 'splh',
        header: 'SPLH ($)',
        cell: (info) => (info.getValue() || 0).toFixed(2),
        size: 90
      },
      {
        accessorKey: 'laborPct',
        header: 'Labor %',
        cell: (info) => (info.getValue() || 0).toFixed(1),
        size: 80
      }
    ],
    []
  );

  /**
   * Select columns based on view mode
   */
  const columns = viewMode === 'shift-level' ? shiftLevelColumns : dailyAggregateColumns;

  /**
   * Initialize TanStack Table
   */
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 25
      }
    }
  });

  /**
   * Handle page size change
   */
  const handlePageSizeChange = (event) => {
    table.setPageSize(Number(event.target.value));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Loading data...
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Compact View Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontSize: '0.75rem',
              borderColor: '#7C3AED',
              color: '#7C3AED',
              '&.Mui-selected': {
                backgroundColor: '#7C3AED',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#6D28D9'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(124, 58, 237, 0.04)'
              }
            }
          }}
        >
          <ToggleButton value="shift-level">By Shift</ToggleButton>
          <ToggleButton value="daily-aggregate">By Date</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table Container with horizontal scroll */}
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
          }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'left',
                      backgroundColor: 'rgba(124, 58, 237, 0.08)',
                      borderBottom: '2px solid #7C3AED',
                      fontWeight: 600,
                      color: '#7C3AED',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {header.column.getIsSorted() === 'asc' ? (
                            <ArrowUpward sx={{ fontSize: 16, color: '#7C3AED' }} />
                          ) : (
                            <ArrowDownward sx={{ fontSize: 16, color: '#7C3AED' }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                style={{
                  borderBottom: '1px solid rgba(224, 224, 224, 1)'
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      padding: '8px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* Pagination Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          pt: 2,
          borderTop: '1px solid rgba(224, 224, 224, 1)'
        }}
      >
        {/* Page size selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          <FormControl size="small">
            <Select
              value={table.getState().pagination.pageSize}
              onChange={handlePageSizeChange}
              sx={{
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6D28D9'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#7C3AED'
                }
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Page info and navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} (
            {tableData.length} total records)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              style={{
                padding: '6px 12px',
                backgroundColor: table.getCanPreviousPage() ? '#7C3AED' : '#E0E0E0',
                color: table.getCanPreviousPage() ? 'white' : '#999',
                border: 'none',
                borderRadius: '4px',
                cursor: table.getCanPreviousPage() ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem'
              }}
            >
              First
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              style={{
                padding: '6px 12px',
                backgroundColor: table.getCanPreviousPage() ? '#7C3AED' : '#E0E0E0',
                color: table.getCanPreviousPage() ? 'white' : '#999',
                border: 'none',
                borderRadius: '4px',
                cursor: table.getCanPreviousPage() ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem'
              }}
            >
              Previous
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              style={{
                padding: '6px 12px',
                backgroundColor: table.getCanNextPage() ? '#7C3AED' : '#E0E0E0',
                color: table.getCanNextPage() ? 'white' : '#999',
                border: 'none',
                borderRadius: '4px',
                cursor: table.getCanNextPage() ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem'
              }}
            >
              Next
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              style={{
                padding: '6px 12px',
                backgroundColor: table.getCanNextPage() ? '#7C3AED' : '#E0E0E0',
                color: table.getCanNextPage() ? 'white' : '#999',
                border: 'none',
                borderRadius: '4px',
                cursor: table.getCanNextPage() ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem'
              }}
            >
              Last
            </button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AtomicDataTable;
